import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { state as stateApi } from '../api/client';
import { fmtInt } from '../lib/format';
import { useT, format } from '../i18n';

const TOP_N = 6;

export function TeamBar() {
  const rows = useData(s => s.rows);
  const team = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const factor = useFilters(s => s.spToPtFactor);

  const t = useT();
  const teams = useMemo(() => sortedByCount(countBy(rows, 'Team Name')), [rows]);
  const topTeams = teams.slice(0, TOP_N);
  const restTeams = teams.slice(TOP_N);

  function pickTeam(v: string) {
    setFilters({ team: v });
  }

  async function onFactorChange(v: string) {
    const n = parseFloat(v.replace(',', '.'));
    const next = isFinite(n) && n > 0 ? n : 1.0;
    setFilters({ spToPtFactor: next });
    try {
      await stateApi.saveConfig({ spToPtFactor: next });
    } catch {
      // still ok – frontend lebt mit dem lokalen Wert weiter
    }
  }

  if (!rows.length) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
        <GroupsIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" color="text.secondary">{t.teamBar.label}</Typography>
      </Stack>

      <Chip
        label={format(t.teamBar.allTeams, { count: fmtInt(rows.length) })}
        color={team === '' ? 'primary' : 'default'}
        variant={team === '' ? 'filled' : 'outlined'}
        onClick={() => pickTeam('')}
        clickable
      />

      {topTeams.map(([name, count]) => (
        <Chip
          key={name}
          label={`${name} (${count})`}
          color={team === name ? 'primary' : 'default'}
          variant={team === name ? 'filled' : 'outlined'}
          onClick={() => pickTeam(name)}
          clickable
        />
      ))}

      {restTeams.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="team-rest-label">{t.teamBar.otherTeams}</InputLabel>
          <Select
            labelId="team-rest-label"
            label={t.teamBar.otherTeams}
            value={restTeams.some(([n]) => n === team) ? team : ''}
            onChange={(e) => pickTeam(e.target.value)}
            displayEmpty
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {restTeams.map(([name, count]) => (
              <MenuItem key={name} value={name}>{name} ({count})</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box sx={{ flex: 1 }} />

      <TextField
        size="small"
        label={t.teamBar.spToPtLabel}
        type="number"
        value={factor}
        onChange={(e) => onFactorChange(e.target.value)}
        slotProps={{
          htmlInput: { min: 0.05, step: 0.05, style: { width: 70 } },
          input: { endAdornment: <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{t.common.ptSuffix}</Typography> },
        }}
        sx={{ width: 130 }}
      />
    </Box>
  );
}
